import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  SignalOriginType,
  ExternalSource,
  SignalVisibilityType,
  SEVERITY_LABELS,
} from '@momentum/types';
import { useCreateSignal } from '@/shared/hooks/useSignals';
import { useSignalReview } from '../context/useSignalReview';

interface UseCreateSignalsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function useCreateSignals({
  open,
  onOpenChange,
}: UseCreateSignalsProps) {
  const { t } = useTranslation('knowledge');
  const { signals } = useSignalReview();
  const createSignalMutation = useCreateSignal();
  const [isCreating, setIsCreating] = useState(false);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setIsCreating(false);
    }
  }, [open]);

  const handleConfirm = useCallback(async () => {
    const signalsToCreate = signals.filter((signal) => !signal.excluded);
    if (signalsToCreate.length === 0) return;

    setIsCreating(true);

    try {
      await Promise.all(
        signalsToCreate.map((signal) =>
          createSignalMutation.mutateAsync({
            title: signal.title,
            context: signal.context,
            origin: {
              type: SignalOriginType.EXTERNAL,
              source: ExternalSource.WORD,
              fileName: signal.fileName,
              sourceContext: signal.sourceContext,
              sourceQuote: signal.sourceQuote,
            },
            visibility:
              signal.visibility === SignalVisibilityType.PRIVATE
                ? {
                    type: SignalVisibilityType.PRIVATE,
                    viewableBy: signal.viewableBy,
                  }
                : { type: SignalVisibilityType.PUBLIC },
            severity: signal.severity ?? SEVERITY_LABELS.Medium,
            missability: signal.missability,
            isAnonymous: signal.isAnonymous,
            applicabilityScope: signal.applicabilityScope,
            category: signal.category,
            informedBy: signal.informedBy,
            reasoning: signal.reasoning,
          })
        )
      );

      toast.success(
        signalsToCreate.length === 1
          ? t('importModal.successSingle')
          : t('importModal.successMultiple', {
              count: signalsToCreate.length,
            })
      );
      onOpenChange(false);
    } catch {
      toast.error(t('importFileError'));
    } finally {
      setIsCreating(false);
    }
  }, [signals, createSignalMutation, t, onOpenChange]);

  return { isCreating, handleConfirm };
}
